import { useContext } from 'react';
import FormContext from './FormContext';
import type { FormContextValues, FormState } from './Form.types';

/**
 * Custom hook to access form context values.
 * Provides direct access to all form state management functions and values.
 * 
 * @throws {Error} If used outside of a Form component
 * @returns {FormContextValues} Object containing form state and control functions
 * 
 * @example
 * ```tsx
 * const MyCustomField = () => {
 *   const { 
 *     formState, 
 *     formErrors, 
 *     updateFieldValue,
 *     resetForm 
 *   } = useForm();
 *   
 *   return (
 *     <div>
 *       <input 
 *         value={formState.myField || ''}
 *         onChange={(e) => updateFieldValue('myField', e.target.value)}
 *       />
 *       {formErrors.myField && <span>{formErrors.myField}</span>}
 *     </div>
 *   );
 * };
 * ```
 */
export const useForm = <T extends FormState = FormState>(): FormContextValues<T> => {
  const context = useContext(FormContext);
  
  if (!context) {
    throw new Error('useForm must be used within a Form component. Wrap your component with <Form> to use this hook.');
  }
  
  // Type assertion is safe here because we know the context exists
  // and the generic type T extends FormState
  return context as FormContextValues<T>;
};

/**
 * Type-safe version of useForm that returns properly typed form context
 * 
 * @example
 * ```tsx
 * interface MyFormData {
 *   name: string;
 *   email: string;
 *   age: number;
 * }
 * 
 * const MyComponent = () => {
 *   const form = useFormTyped<MyFormData>();
 *   // form.formState is now typed as MyFormData
 *   // form.formErrors is now typed as FormErrors<MyFormData>
 * };
 * ```
 */
export const useFormTyped = <T extends FormState>() => useForm<T>();